import idx from "idx";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../scss/project-management.scss";
import { Paper } from "@material-ui/core";
import OverviewDetails from "../overview-details";
import ProjectSummary from "../project-summary";
import ProjectMenu from "../project-menu";
import { Button } from "@mui/material";
import TaskList from "../task-list";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { getProjectSummaryData } from "../utils/get-project-summary";
import { resolveProjectType } from "../utils/resolve-project-type";
import { editProject } from "../../../actions/project-management.action";
import { updateProjectAction, clearProjectAction } from "../../../slice/project-summary";

export default function ViewProject(props) {

    const [viewProjectState, setViewProjectState] = useState({
        anchorEl: null,
        open: false,
        customstyle: "",
        statusLoading: false,
        pageLoading: false,
        error: null,
        isFadingOut: false,
    })

    const { projectId } = useParams();

    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();
    const projectSummary = useSelector(state => state.projectSummary) || {}
    const projectCard = useSelector(state => state.projectSummary.projectcard) || {}
    const progressCard = useSelector(state => state.projectSummary.progresscard) || {}
    const financialCard = useSelector(state => state.projectSummary.financialCard) || {}
    const overviewCards = useSelector(state => state.projectSummary.overviewCards) || {}
    const projectState = useSelector(state => state.project);

    useEffect(() => {
        if (!projectId) return;
        
        // Clear previous state immediately to prevent data ghosting
        dispatch(clearProjectAction());
        setViewProjectState(prev => ({ 
            ...prev, 
            pageLoading: true, 
            error: null 
        }));
        
        const fetchData = async () => {
            try {
                await getProjectSummaryData({ projectId, dispatch });
            } catch (error) {
                setViewProjectState(prev => ({ 
                    ...prev, 
                    error: error.message || 'Failed to load project data' 
                }));
            } finally {
                // Start fade-out animation before hiding loading state
                setViewProjectState(prev => ({ 
                    ...prev, 
                    isFadingOut: true 
                }));
                
                // Wait for fade-out animation to complete before hiding
                setTimeout(() => {
                    setViewProjectState(prev => ({ 
                        ...prev, 
                        pageLoading: false,
                        isFadingOut: false 
                    }));
                }, 300);
            }
        };
        
        fetchData();
        
        // Cleanup function to clear state when component unmounts or projectId changes
        return () => {
            dispatch(clearProjectAction());
        };
    }, [projectId, dispatch])

    useEffect(() => {
        if (location.state?.openUploadCostModal) {
            setViewProjectState(prev => ({
                ...prev,
                anchorEl: true
            }));
        }
    }, [location.state]);


    const handleMenu = () => {
        setViewProjectState(prev => ({
            ...prev,
            anchorEl: true
        }));
    };

    // const ViewrfqDialogHandler = () => {
    //     navigate(`${props.moduleURL}/rfqview/${viewProjectState.projectId}`);
    // };

    const handleClose = () => {
        setViewProjectState(prev => ({
            ...prev,
            anchorEl: null, hasStages: null
        }));
    };
    const viewProjectList = () => {
        let type = resolveProjectType(
            idx(projectSummary, _ => _.project.projectDataType) || idx(projectSummary, _ => _.project.res.projectType),
            projectState
        );
        navigate(`/projects?project-type=${type}`);
    };

    const handleStatusChange = async (newStatus) => {
        setViewProjectState(prev => ({ ...prev, statusLoading: true }))
        try {
            await dispatch(editProject({
                status: newStatus
            }, projectId))
            dispatch(updateProjectAction({ status: newStatus }))
        } finally {
            setViewProjectState(prev => ({ ...prev, statusLoading: false }))
        }
    }

    // Show loading state while fetching data
    if (viewProjectState.pageLoading || viewProjectState.isFadingOut) {
        return (
            <div className={`viewProject viewProject-ch loading-container ${viewProjectState.isFadingOut ? 'fade-out' : ''}`}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center loading-content">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading project data...</span>
                        </div>
                        <h5 className="text-primary fw-bold mb-2">Loading Project Data</h5>
                        <p className="text-dark" style={{ fontSize: '1rem', fontWeight: '500' }}>
                            Please wait while we fetch your project information...
                        </p>
                        <div className="mt-3">
                            <small className="text-muted">This may take a few seconds</small>
                        </div>
                    </div>
                </div>
                <style jsx>{`
                    .loading-container {
                        animation: fadeIn 0.3s ease-in;
                    }
                    .loading-content {
                        animation: slideUp 0.4s ease-out;
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { 
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to { 
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .loading-container.fade-out {
                        animation: fadeOut 0.3s ease-out forwards;
                    }
                    @keyframes fadeOut {
                        from { opacity: 1; }
                        to { opacity: 0; }
                    }
                `}</style>
            </div>
        );
    }

    // Show error state if data fetch failed
    if (viewProjectState.error) {
        return (
            <div className="viewProject viewProject-ch">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="alert alert-danger" role="alert">
                            <h4 className="alert-heading">Error Loading Project</h4>
                            <p>{viewProjectState.error}</p>
                            <hr />
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => window.location.reload()}
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="viewProject viewProject-ch">
            {Boolean(viewProjectState.anchorEl) && <ProjectMenu
                open={Boolean(viewProjectState.anchorEl)}
                anchorEl={viewProjectState.anchorEl}
                projectId={projectId}
                type={idx(projectSummary, _ => _.project.projectType)}
                dlp={idx(projectSummary, _ => _.project.dlp)}
                completed={idx(projectSummary, _ => _.project.completed)}
                lost={idx(projectSummary, _ => _.project.lost)}
                archived={idx(projectSummary, _ => _.project.archived)}
                projectType={idx(projectSummary, _ => _.project.projectDataType)}
                writePermission={props.updatePermission}
                history={navigate}
                onClose={handleClose}
                hasStages={idx(projectSummary, _ => _.project.hasStages)}
                menuType={"View"}
                status={idx(projectSummary, _ => _.project.status)}
                projectName={idx(projectSummary, _ => _.project.name)}
                customstyle={"project-view-menuStyle"}
            />}

            <div className="row">
                <div className="w-auto">
                    <div className="col-12 breadcrumb-block p-0">
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={viewProjectList}>PROJECTS</Breadcrumb.Item>
                            <Breadcrumb.Item active>{idx(projectSummary, _ => _.project.projectName)}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </div>
                <div className="w-auto ms-auto mt-auto">
                    <Button
                        variant="outlined"
                        onClick={handleMenu}
                        aria-controls="simple-menu"
                        aria-haspopup="true"
                    >
                        ...
                    </Button>
                </div>
            </div>

            <div className="view-project-page">
                {/* ---------------------progress financial---------------------------- */}
                <div className="mobileViewProject" style={{ background: "transparent" }}>
                    <Paper>
                        <ProjectSummary
                            projectId={projectId}
                            projectName={idx(projectCard, _ => _.projectName) || ""}
                            siteAddress={idx(projectCard, _ => _.addressBreakdown.formatted_address) || ""}
                            customerName={idx(projectCard, _ => _.customer) || ""}
                            contactData={idx(projectCard, _ => _.contact) || ""}
                            projectManagerName={idx(projectCard, _ => _.assignedTo) || ""}
                            status={idx(projectSummary, _ => _.project.status)}
                            onStatusChange={handleStatusChange}
                            statusLoading={viewProjectState.statusLoading}
                            quoteType={idx(projectSummary, _ => _.project.quoteType)}
                            progressCostMaterial={idx(progressCard, _ => _.remainingMaterialBudget)}
                            progressCostLabor={idx(progressCard, _ => _.remainingResourceBudget)}
                            progressCostSubcontractor={idx(progressCard, _ => _.remainingContractorBudget)}
                            materialAllowance={idx(progressCard, _ => _.materialAllowance)}
                            resourceAllowance={idx(progressCard, _ => _.resourceAllowance)}
                            contractorAllowance={idx(progressCard, _ => _.contractorAllowance)}
                            materialActualSpending={idx(progressCard, _ => _.materialActualSpending)}
                            resourceActualSpending={idx(progressCard, _ => _.resourceActualSpending)}
                            contractorActualSpending={idx(progressCard, _ => _.contractorActualSpending)}
                            timeSpend={idx(progressCard, _ => _.timeSpend)}
                            timeAllocated={idx(progressCard, _ => _.timeAllocated)}
                            totalContract={idx(financialCard, _ => _.contract)}
                            totalVariationAmount={idx(financialCard, _ => _.variations)}
                            totalRevisedContractCost={idx(financialCard, _ => _.revisedContract)}
                            totalClaimAmount={idx(financialCard, _ => _.claimed)}
                            totalRetentionAmount={idx(financialCard, _ => _.retentionHeld)}
                            balanceAmount={idx(financialCard, _ => _.balance)}
                        />
                    </Paper>
                </div>
                {/* ----------------------task cards-------------------------- */}
                <div className="mobileViewProject" style={{ background: "transparent" }}>
                    <Paper className="main" style={{ boxShadow: "none", background: "transparent" }}>
                        <OverviewDetails
                            // handleRFQClick={ViewrfqDialogHandler}
                            totalRFQSend={idx(overviewCards, _ => _.totalRFQSend)}
                            totalVariation={idx(overviewCards, _ => _.variationCount)}
                            // handlePoClick={poDialogHandler}
                            projectId={projectId}
                            quoteType={idx(projectSummary, _ => _.project.quoteType)}
                            progressClaimCount={idx(overviewCards, _ => _.budgetCount)}
                            purchaseOrdersCount={idx(overviewCards, _ => _.purchaseOrderCount)}
                            workOrdersCount={idx(overviewCards, _ => _.workOrderCount)}
                            invoicesCount={idx(overviewCards, _ => _.invoiceCount)}
                            vendorInvoiceCount={idx(overviewCards, _ => _.vendorInvoiceCount)}
                            financePermission={props.financePermission}
                            rFinancePermission={props.rFinancePermission}
                            rSupplierPermission={props.rSupplierPermission}
                            wSupplierPermission={props.wSupplierPermission}
                            rContractorPermission={props.rContractorPermission}
                            wContractorPermission={props.wContractorPermission}
                            projectName={idx(projectSummary, _ => _.project.projectName)}
                            projectType={idx(projectSummary, _ => _.project.projectDataType)}
                        />
                    </Paper>
                </div>

                {/* ---------------------table--------------------------- */}
                <div className="table-responsive tender-table">
                    <TaskList
                        {...props}
                        projectId={projectId}
                    />
                </div>
            </div>
        </div>
    );
}
